<template>
  <div>
    <v-container fluid>
      <v-row dense class="pb-1">
        <v-col cols="3">
          <date-range :from.sync="from" :to.sync="to" v-model="dateDisplay" @change="refresh()" option="Last 30 days" hide-details/>
        </v-col>
        <v-col cols="2">
          <v-autocomplete label="Product units" dense outlined :items="unitsList" v-model="units" hide-details @change="refresh()"/>
        </v-col>
        <v-col cols="3">
          <multi-level-select label="Item" :menuData="itemsList" :first.sync="type" :second.sync="id" caption outlined @change="refresh()" hide-details no-validation/>
        </v-col>
        <v-col class="text-right pr-2">
          <v-btn icon @click="printHtml(report)"><v-icon>mdi-printer</v-icon></v-btn>
          <v-tooltip bottom open-delay="1000" v-if="company.allowCSVExport">
            <template v-slot:activator="{on}">
              <v-btn icon @click="exportCSV" v-on="on"><v-icon>mdi-download</v-icon></v-btn>
            </template>
            Export as CSV file
          </v-tooltip>
        </v-col>
      </v-row>
      <v-row dense class="pb-0">
        <v-col cols="2">
          <v-select label="Source" hide-details outlined dense class="mt-1" v-model="source" :items="[{text:'Shipments',value:'shipment'},{text:'Invoices',value:'invoice'},{text:'Sales orders',value:'salesOrder'}]"  @change="refresh()"/>
        </v-col>
        <v-col cols="2" offset="1">
          <v-switch label="Exclude $0 sales" hide-details dense class="mt-1 ml-1" v-model="exclude0" @change="refresh()"/>
        </v-col>
      </v-row>
    </v-container>

    <v-card class="mx-4 pa-4 overflow-y" height="calc(100vh - 228px)">
      <span v-html="report"/>
    </v-card>
  </div>
</template>

<script>
import fileSaver from 'file-saver';
import printHtml from '@/mixins/printHtml'
import createHierarchyList from '@/mixins/createHierarchyList'

export default {
  name: 'ReportsSalesDetailsByCustomer',
  mixins: [printHtml,createHierarchyList],
  data() {
    return {
      itemsList: [{name:'All',id:''},{divider:true}],
      type: '',
      id: '',
      exclude0: false,
      source: 'shipment',
      from: null,
      to: null,
      dateDisplay: '',
      unitsList: [{text:'bottles',value:'bottles'},{text:'units',value:'sellingUnits'},
                  {text:'cases',value:'cases'},{text:'pallets',value:'pallets'},
                  {text:'litres',value:'litres'},{text:'laa',value:'laa'}],
      precisionList: {bottles:0, sellingUnits:0, cases:1, pallets:1, litres:3, laa:3},
      units: 'bottles',
      loading: true,
      previousCustomer: null,
      totalValue: 0,
      lines: [],
      report: ''
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    }
  },
  async created() {
    this.from = this.$route.query.fromDate;
    this.to = this.$route.query.toDate;
    this.id = this.$route.query.id ?? '';
    this.type = this.$route.query.type ?? '';
    this.units = this.$route.query.units ?? 'bottles';
    this.$emit('name', 'Sales details by customer')
    const productCategories = [];
    const itemCategories = [];
    const products = [];
    const items = [];
    const bundles = [];
    const spirits = [];
    const wines = [];
    const nonExcises = [];
    await Promise.all([
      this.$lazyListLoad(productCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Product'}]),
      this.$lazyListLoad(itemCategories, 'categories2', 'id,name,parent{id}', 100, [], [{name:'type',value:'Item'}]),
      this.$lazyListLoad(products, 'products2', 'id,name,category{id}', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(bundles, 'bundles2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(items, 'items2', 'id,name,category{id}', 100, [{field:'active', comparator:'eq', value:'true'},{field:'sold', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(spirits, 'spirits2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(wines, 'wines2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}]),
      this.$lazyListLoad(nonExcises, 'nonExcises2', 'id,name', 100, [{field:'active', comparator:'eq', value:'true'}])
    ]);

    this.itemsList.push({name:'Product',id:'Product',menu:this.menuForProducts(products, productCategories)});
    if (bundles.length)
      this.itemsList.push({name:'Bundle',id:'Bundle',menu:bundles});
    if (items.length) {
      if (itemCategories.length) {
        const categories = this.createHierarchyList(itemCategories);
        for (const category of categories) {
          const sub = items.filter(item => item.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//          this.itemsList.push({name:'Item',id:'Item',menu:[...categories.filter(category => !category.disabled)]});
        this.itemsList.push({name:'Item',id:'Item',menu:categories});
        const leftOver = items.filter(item => !item.category?.id);
        if (leftOver.length)
          this.itemsList[this.itemsList.length-1].menu.push({divider:true},{name:'Other items',id:'x',menu:leftOver});
      }
      else
        this.itemsList.push({name:'Item',id:'Item',menu:items});
    }
    this.itemsList.push({name:'Spirit', id:'Spirit',menu:spirits});
    if (wines.length)
      this.itemsList.push({name:'Wine', id:'Wine',menu:wines});
    if (nonExcises.length)
      this.itemsList.push({name:'Non-excise', id:'NonExcise',menu:nonExcises});

    if (this.$route.query.from && this.$route.query.to) {
      this.from = this.$route.query.from;
      this.to = this.$route.query.to;
    }
  },
  methods: {
    menuForProducts(products, productCategories) {
      if (productCategories.length) {
        const categories = this.createHierarchyList([...productCategories]);
        for (const category of categories) {
          const sub = products.filter(product => product.category?.id === category.id);
          if (sub.length)
            category.menu = sub;
          else
            category.disabled = true;
        }
//        const p = [...categories.filter(category => !category.disabled)];
        const p = categories;
        const leftOver = products.filter(product => !product.category?.id);
        if (leftOver.length)
          p.push({divider:true},{name:'Other products',id:'x',menu:leftOver});
        return p;
      }
      else
        return products;
    },
    async refresh() {
      this.loading = true;
      const results = await this.$apiCall(`query ReportsSalesDetailsByCustomer($from:Date!,$to:Date!,$itemId:ID, $exclude0:Boolean,  $source:String!) {
                                              salesDetailsReportByCustomer(from:$from,to:$to,itemId:$itemId,exclude0:$exclude0,source:$source) {
                                                name,product,units,precision
                                                quantity,bottles,sellingUnits,cases,pallets,litres,laa,value
                                                customer,date,invoiceNumber,lot,
                                                city,postalCode
                                              }
                                            }`,
                                          {from:this.from,to:this.to,itemId:this.type ? this.id : null,exclude0:this.exclude0,source:this.source});
      if (!results.error)
        this.lines = results.data.salesDetailsReportByCustomer;

      const lines = [
                  `<style>
                    @page {size:landscape}
                    .report {font-family: Roboto, sans-serif; width:100%;border-spacing:0;font-size:12px}
                    .report th {height:36px;padding:0 16px;vertical-align:middle}
                    .report .spacer {height:18px}
                    .report td {height:36px;padding:0 16px;vertical-align:middle}
                    .report .title {font-size:18px;text-transform:uppercase}
                    .report .sup-title {font-weight:normal;font-size:22px}
                    .report .sub-title {font-weight:normal;height:inherit;padding-bottom:20px;font-size:14px}
                    .report .header th {border-bottom:1px solid #000;margin-bottom:20px}
                    .report .text-left {text-align:left}
                    .report .text-right {text-align:right}
                    .report .indent {text-indent:20px}
                    .report .font-weight-bold {font-weight:bold}
                    .report .total-row {font-weight:bold}
                  </style>
                   <table class="report">
                     <thead>
                        <tr>
                          <th colspan="6" class="sup-title">${this.company.name}</th>
                        </tr>
                        <tr>
                          <th colspan="6" class="title">
                            Sales details by customer
                          </th>
                        </tr>
                        <tr>
                           <th colspan="6" class="sub-title">${this.dateDisplay}</th>
                        </tr>
                        <tr class="header">
                          <th width="30%" class="text-left">Customer/Item</th>
                          ${this.source === 'salesOrder' ? ``
                                                        : `<th width="10%" class="text-left">Lot</th>
                                                           <th width="15%" class="text-left">Invoice #</th>`}
                          <th width="15%" class="text-left">Date</th>
                          <th width="15%" class="text-right">Quantity</th>
                          <th width="15%" class="text-right">Value</th>
                        </tr>
                      </thead>
                      <tbody>`
      ];
      this.previousCustomer = null;
      for (const line of this.lines) {
        if (line.customer !== this.previousCustomer) {
          this.totalLine(lines);
          lines.push(`<tr><td colspan="${this.source === 'salesOrder' ? 2 : 4}">${line.customer}</td></tr>`);
          this.previousCustomer = line.customer;
        }
        lines.push(`<tr>
                      <td class="indent">${line.name}</td>`);
        if (this.source !== 'salesOrder')
          lines.push(`  <td>${line.lot ?? ''}</td>
                        <td>${line.invoiceNumber ?? ''}</td>`);
        lines.push(`<td>${this.$formatDateTime(line.date)}</td>
                      <td class="text-right">`);
        if (line.product) {
          const precision = this.precisionList[this.units] ?? line.precision;
          lines.push(`${this.$number(line[this.units],precision)} ${this.unitsList.find(unit => unit.value === this.units).text}`);
        }
        else
          lines.push(`${this.$number(line.quantity,line.precision)} ${line.units}`);
        lines.push(`</td>
                    <td class="text-right">$${this.$number(line.value,2)}</td>
                    </tr>`);

        this.totalValue += line.value;
      }
      this.totalLine(lines);

      lines.push(`</tbody>`);
      lines.push(`</table>`);
      this.report = lines.join('');
      this.loading = false;
    },
    totalLine(lines) {
      if (!this.previousCustomer)
        return;

      lines.push(`<tr class="total-row">
        <td colspan="${this.source === 'salesOrder' ? 3 : 5}" class="text-right">Total</td>
        <td class="text-right">$${this.$number(this.totalValue,2)}</td>
      </tr>`);
      this.totalValue = 0;
    },

    exportCSV() {
      const lines = [ `"${this.company.name}",,,,,,,,`,
                      `"Sales details by customer",,,,,,,,`,
                      `"${this.dateDisplay}",,,,,,,,`,
                      `"Customer","City","Postal code","Item","Lot","Invoice","Date","Quantity","Value"`
                    ];
      for (const line of this.lines) {
        const quantity = line.product ? `${this.$number(line[this.units],line.precision)}` :
                                        `${this.$number(line.quantity,line.precision)}`;
        lines.push(`"${line.customer}","${line.city}","${line.postalCode}","${line.name}","${line.lot??''}","${line.invoiceNumber??''}","${line.date}","${quantity}","${this.$number(line.value,2)}"`);
      }

      // Write CSV file
      const blob = new Blob([lines.join('\r\n')], {type: "text/plain;charset=utf-8"});
      fileSaver.saveAs(blob, 'Sales by customer.csv');
    }
  }
}
</script>